import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { UserProfile } from '@/types/workout';
import { hybridFirestoreService } from '@/services/hybridFirestoreService';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to manually refresh profile data
  const refreshProfile = async (): Promise<void> => {
    if (!user) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profile = await hybridFirestoreService.getUserProfile(user.uid);

      setUserProfile(profile);
    } catch (error) {
      // Failed to refresh profile
      setError('Failed to refresh profile data');
    } finally {
      setLoading(false);
    }
  };

  // Global debug function for browser console
  // Debug function removed for production

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setError(null);

      // Clean up previous profile subscription if it exists
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        setLoading(true);

        // Set up profile subscription with improved error handling and direct Firestore
        const setupProfileSubscription = async (retryCount = 0) => {
          try {
            // Force direct Firestore for profile subscriptions to ensure reliability
            (hybridFirestoreService as any).useDirectFirestore = true;

            // Subscribe to user profile changes
            unsubscribeProfile = hybridFirestoreService.subscribeToUserProfile(user.uid, (profile) => {
              setUserProfile(profile);
              setLoading(false);
            });

          } catch (error) {
            if (retryCount < 3) {
              const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s

              setTimeout(() => {
                if (user && user.uid) {
                  setupProfileSubscription(retryCount + 1);
                }
              }, delay);
            } else {
              // Failed to load user profile after retries
              setError('Failed to load user profile. Please refresh the page.');
              setLoading(false);
            }
          }
        };

        // Start the subscription setup
        setupProfileSubscription();
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    error,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
